/**
 * Who can sign in, and what they may do.
 *
 * Deliberately plain. There is no invitation email in the pilot, so the
 * one-time link is shown on screen for an administrator to pass on through a
 * channel they trust — stated as such rather than dressed up as a feature.
 */
import { useCallback, useState } from 'react';
import { api, ApiRequestError, type InviteResult } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { Panel, ToneChip } from '../components/Primitives';
import { EmptyState, ErrorState, SkeletonPanel } from '../components/States';
import { relativeTime } from '../lib/format';
import { useAuth } from '../lib/auth';

const ROLES = [
  { value: 'tenant_admin', label: 'Administrator', detail: 'Everything, including accounts.' },
  {
    value: 'integration_admin',
    label: 'Integration administrator',
    detail: 'Connections and credentials.',
  },
  { value: 'project_owner', label: 'Project owner', detail: 'Decisions, stations, takeover.' },
  { value: 'operator', label: 'Operator', detail: 'Leases, dispatch, day-to-day operation.' },
  { value: 'contributor', label: 'Contributor', detail: 'Read, and resolve blockers.' },
  { value: 'viewer', label: 'Viewer', detail: 'Read only.' },
];

export function Accounts() {
  const { user } = useAuth();
  const users = useProjection(() => api.users(), []);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('viewer');
  const [link, setLink] = useState<{ result: InviteResult; who: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const act = useCallback(
    async (work: () => Promise<unknown>) => {
      setError(null);
      try {
        await work();
        users.reload();
      } catch (err) {
        setError(err instanceof ApiRequestError ? err.message : 'That did not work.');
      }
    },
    [users],
  );

  const invite = useCallback(async () => {
    if (!email.trim() || !displayName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.inviteUser(email.trim(), displayName.trim(), role);
      setLink({ result: response.data, who: email.trim() });
      setEmail('');
      setDisplayName('');
      users.reload();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'The account could not be created.');
    } finally {
      setBusy(false);
    }
  }, [email, displayName, role, users]);

  if (users.loading) return <SkeletonPanel rows={5} />;
  if (users.error) return <ErrorState error={users.error} onRetry={users.reload} />;
  const rows = users.data!.users;

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">Access</div>
          <h1 className="topstrip__title">Accounts</h1>
          <p className="small dim">
            An invited account exists before it has a password and cannot be signed in to until
            someone uses its link. Disabling an account ends its sessions immediately.
          </p>
        </div>
      </header>

      {error && (
        <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
          {error}
        </p>
      )}

      <Panel title="Invite someone" subtitle="They set their own password through a one-time link.">
        <div className="inline" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="btn btn--sm"
            style={{ minWidth: 200 }}
            placeholder="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-label="Email"
          />
          <input
            className="btn btn--sm"
            style={{ minWidth: 180 }}
            placeholder="Name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            aria-label="Name"
          />
          <select
            className="btn btn--sm"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            aria-label="Role"
          >
            {ROLES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={busy || !email.trim() || !displayName.trim()}
            onClick={() => void invite()}
          >
            Create the account
          </button>
        </div>
        <p className="tiny dim" style={{ marginTop: 8 }}>
          {ROLES.find((option) => option.value === role)?.detail}
        </p>

        {link && (
          <div className="panel" data-tone="attention" style={{ padding: 12, marginTop: 12 }}>
            <div className="label">
              One-time link for {link.who}. It is shown once and expires{' '}
              {relativeTime(link.result.expiresAt)}.
            </div>
            <p className="mono small" style={{ wordBreak: 'break-all', margin: '8px 0' }}>
              {window.location.origin}
              {link.result.setPasswordPath}
            </p>
            <p className="tiny dim">
              Send it over a channel you trust. Anyone holding it can set this account’s password.
            </p>
            <button type="button" className="btn btn--sm" onClick={() => setLink(null)}>
              I have sent it
            </button>
          </div>
        )}
      </Panel>

      <Panel title={`Accounts (${rows.length})`} flush>
        <div className="rows">
          {rows.length === 0 ? (
            <div style={{ padding: 16 }}>
              <EmptyState tone="neutral" title="No accounts" body="Nobody can sign in yet." />
            </div>
          ) : (
            rows.map((account) => {
              const self = account.id === user?.id;
              return (
                <div
                  key={account.id}
                  className="row"
                  style={{ gridTemplateColumns: '1fr auto' }}
                  data-tone={account.disabledAt ? 'neutral' : 'active'}
                >
                  <div className="row__stack row__cell" data-label="Account">
                    <span className="inline inline--tight">
                      <ToneChip tone={account.disabledAt ? 'neutral' : 'active'}>
                        {account.disabledAt
                          ? 'DISABLED'
                          : account.passwordSetAt
                            ? 'ACTIVE'
                            : 'INVITED'}
                      </ToneChip>
                      <strong>{account.displayName}</strong>
                      {self && <span className="tiny dim">you</span>}
                    </span>
                    <span className="row__sub mono truncate">{account.email}</span>
                    <span className="row__sub">
                      {account.lastSignInAt
                        ? `last signed in ${relativeTime(account.lastSignInAt)}`
                        : 'never signed in'}
                      {account.activeSessions > 0
                        ? ` · ${account.activeSessions} open ${account.activeSessions === 1 ? 'session' : 'sessions'}`
                        : ''}
                    </span>
                  </div>
                  <div
                    className="row__stack row__cell"
                    data-label="Access"
                    style={{ alignItems: 'flex-end', gap: 6 }}
                  >
                    <select
                      className="btn btn--sm"
                      value={account.role}
                      aria-label={`Role for ${account.displayName}`}
                      onChange={(event) =>
                        void act(() => api.setUserRole(account.id, event.target.value))
                      }
                    >
                      {ROLES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="inline inline--tight">
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() =>
                          void act(async () => {
                            const response = await api.resetUserPassword(account.id);
                            setLink({ result: response.data, who: account.email });
                          })
                        }
                      >
                        New link
                      </button>
                      {account.disabledAt ? (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => void act(() => api.enableUser(account.id))}
                        >
                          Enable
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => void act(() => api.disableUser(account.id))}
                        >
                          Disable
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Panel>
    </>
  );
}
